/**
 * Created by katarinababic on 14.5.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { WorkoutLogAnalytics } from '../../../Model/WorkoutLog/WorkoutLogAnalytics';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { Theme } from '../../../Theme/Theme';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import { getMediaMediumOrSmallest } from '../../../Model/Media/Media';

const HeaderContainer = styled.div<{ imageUri: string }>`
  position: relative;
  width: 100%;
  height: 320px;
  background-image: url(${(props) => props.imageUri});
  background-size: cover;
  background-position: center;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Content = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Title = styled.h2`
  color: ${Theme.Colors.white};
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  text-align: center;
`;

const Subtitle = styled.h3`
  color: ${Theme.Colors.white};
  margin: 4px 0 16px;
  font-size: 16px;
  font-weight: normal;
  text-align: center;
`;

const ParamsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 8px;
`;

const ParamItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 8px 12px;
  color: ${Theme.Colors.white};
  text-align: center;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  background: #293749;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  & > i {
    color: ${Theme.Colors.primary};
    font-size: 24px;
  }
`;

const ParamName = styled.div`
  font-size: 14px;
  margin-bottom: 4px;
`;

const ParamValue = styled.div`
  font-size: 16px;
  font-weight: bold;
`;

export type EndWorkoutOverviewHeaderProps = {
  analytics: WorkoutLogAnalytics;
};

export const EndWorkoutOverviewHeader: React.FC<EndWorkoutOverviewHeaderProps> = observer(({ analytics }) => {
  const { t } = useTrans();

  const workoutImage = analytics.workoutLog?.workout?.image;
  const imageUri = workoutImage ? getMediaMediumOrSmallest(workoutImage) : require('../../../Assets/Images/logo.png');

  const title = t('endWorkoutOverview.congratulations', { defaultValue: 'Congratulations' });
  const subtitle = t('endWorkoutOverview.workoutCompleted', { defaultValue: "You've completed your workout!" });

  const durationName = t('endWorkoutOverview.duration', { defaultValue: 'Duration' });
  const durationValue = dayjs.utc(analytics.totalTimeExercising).format('HH:mm:ss');
  const breakName = t('endWorkoutOverview.break', { defaultValue: 'Break' });
  const breakValue =
    analytics.totalRestTime > 3599999
      ? dayjs.utc(analytics.totalRestTime).format('HH:mm:ss')
      : dayjs.utc(analytics.totalRestTime).format('mm:ss');
  const caloriesName = t('endWorkoutOverview.calories', { defaultValue: 'Calories' });
  const caloriesValue = analytics.totalCaloriesBurnt;
  const weightName = t('endWorkoutOverview.weight', { defaultValue: 'Weight' });
  const weightValue = `${analytics.totalKg}kg`;
  const setsName = t('endWorkoutOverview.sets', { defaultValue: 'Sets' });
  const setsValue = analytics.totalSets;
  const repetitionsName = t('endWorkoutOverview.repetitions', { defaultValue: 'Repetitions' });
  const repetitionsValue = analytics.totalRepetitions || 0;

  const params: { name: string; value: React.ReactNode; icon: string }[] = [
    { name: durationName, value: durationValue, icon: 'mdi-timer' },
    { name: breakName, value: breakValue, icon: 'mdi-pause' },
    { name: caloriesName, value: caloriesValue, icon: 'mdi-fire' },
    { name: weightName, value: weightValue, icon: 'mdi-weight-lifter' },
    { name: setsName, value: setsValue, icon: 'mdi-format-list-numbered' },
    { name: repetitionsName, value: repetitionsValue, icon: 'mdi-repeat' },
  ];
  return (
    <HeaderContainer imageUri={imageUri}>
      <Overlay />
      <Content>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        <ParamsWrapper>
          {params.map(({ name, value, icon }) => (
            <ParamItem key={name}>
              <IconCircle>
                <i className={`mdi ${icon}`} />
              </IconCircle>
              <ParamName>{name}</ParamName>
              <ParamValue>{value}</ParamValue>
            </ParamItem>
          ))}
        </ParamsWrapper>
      </Content>
    </HeaderContainer>
  );
});
